import { unwrapApiResponseData } from "@core/shared/utils/api-response";
import { Endpoint } from "@mongez/http";
import type {
  CertificateDownloadResponse,
  CertificateFormat,
  CertificateVerification,
  HallOfFameResponse,
  MyCertificatesResponse,
  StudentCertificate,
} from "./types";

export const CERTIFICATES_API = {
  mine: "/me/certificates",
  view: (id: number | string) => `/me/certificates/${id}`,
  download: (id: number | string) => `/me/certificates/${id}/download`,
  hallOfFame: "/certificates/hall-of-fame",
  verify: (code: string) => `/certificates/verify/${encodeURIComponent(code)}`,
};

/** Hall-of-fame list limits accepted by the API (home carousel / view-all page). */
export const HALL_OF_FAME_LIMITS = { home: 10, page: 50 } as const;

/**
 * List the logged-in student's certificates, newest first.
 *
 * @example
 * const { records } = await listMyCertificatesService(http, { page: 1 });
 */
export async function listMyCertificatesService(
  http: Endpoint,
  params: { page?: number; limit?: number } = {},
): Promise<MyCertificatesResponse> {
  const response = await http.get(CERTIFICATES_API.mine, {
    params: { page: params.page || 1, limit: params.limit || 24 },
  });
  const payload = unwrapApiResponseData<Partial<MyCertificatesResponse>>(
    response.data,
  );

  return {
    records: Array.isArray(payload?.records) ? payload.records : [],
    paginationInfo: payload?.paginationInfo ?? null,
  };
}

/**
 * Load one of the logged-in student's certificates (404 when not theirs).
 *
 * @example
 * const certificate = await getMyCertificateService(http, 42);
 */
export async function getMyCertificateService(
  http: Endpoint,
  id: number,
): Promise<StudentCertificate> {
  const response = await http.get(CERTIFICATES_API.view(id));
  const payload = unwrapApiResponseData<{ record?: StudentCertificate }>(
    response.data,
  );

  if (!payload?.record) throw new Error("Invalid certificate response");

  return payload.record;
}

/**
 * Short-lived attachment URL of the certificate PNG or PDF.
 *
 * @example
 * const { url } = await getMyCertificateDownloadService(http, 42, "pdf");
 */
export async function getMyCertificateDownloadService(
  http: Endpoint,
  id: number,
  format: CertificateFormat,
): Promise<CertificateDownloadResponse> {
  const response = await http.get(CERTIFICATES_API.download(id), {
    params: { format },
  });

  return unwrapApiResponseData<CertificateDownloadResponse>(response.data);
}

/**
 * Home «لوحة الشرف»: students default to their grade; guests pass a grade.
 *
 * @example
 * const data = await getCertificatesHallOfFameService(http, { limit: 10 });
 */
export async function getCertificatesHallOfFameService(
  http: Endpoint,
  params: { grade?: number; limit?: number } = {},
): Promise<HallOfFameResponse> {
  const response = await http.get(CERTIFICATES_API.hallOfFame, {
    params: {
      ...(params.grade ? { grade: params.grade } : {}),
      limit: params.limit || HALL_OF_FAME_LIMITS.home,
    },
  });
  const payload = unwrapApiResponseData<Partial<HallOfFameResponse>>(
    response.data,
  );

  return {
    grades: Array.isArray(payload?.grades) ? payload.grades : [],
    grade: payload?.grade ?? null,
    hasMore: payload?.hasMore === true,
    certificates: Array.isArray(payload?.certificates)
      ? payload.certificates
      : [],
  };
}

/**
 * Public certificate verification by its QR code.
 *
 * @example
 * const certificate = await verifyCertificateService(http, "K7Q2M-9XW4R");
 */
export async function verifyCertificateService(
  http: Endpoint,
  code: string,
): Promise<CertificateVerification> {
  const response = await http.get(CERTIFICATES_API.verify(code));
  const payload = unwrapApiResponseData<{
    certificate?: CertificateVerification;
  }>(response.data);

  if (!payload?.certificate) throw new Error("Invalid verification response");

  return payload.certificate;
}
